import { hasSubscriptionFlag, hasSegmentFlag } from '@/data/flags/local-flags';
import cloneDeep from 'lodash/cloneDeep';
import defaults from 'lodash/defaults';
import has from 'lodash/has';
import isString from 'lodash/isString';
// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';
import { i18n_t } from '@utils/i18n';
import sanitizeHtml from 'sanitize-html';

const $t = i18n_t;

export const getDefaultSegments = () => ({
    everyone: {
        order: 1,
        default: true,
        name: $t('segments.default.Everyone'),
        id: 'everyone',
        shared: true,
        definition: 'everyone',
        description: $t(
            'segments.default.everyone.Includes all visitors to any app in the subscription, both identified and anonymous',
        ),
        enabled () {
            const self = this;

            return Promise.resolve(self);
        },
        pipeline () {
            return null;
        },
    },
    everyoneExcludingAnonymous: {
        order: 2,
        default: true,
        name: $t('segments.default.Everyone'),
        aside: $t('segments.default.aside.identifiedVisitorsOnly'),
        id: 'everyoneExcludingAnonymous',
        shared: true,
        definition: 'everyoneExcludingAnonymous',
        description: $t(
            'segments.default.everyoneExcludingAnonymous.Includes only known visitors with an assigned Visitor ID',
        ),
        enabled () {
            const self = this;
            const showAnonymous = hasSubscriptionFlag('showAnonymous');

            return Promise.resolve(showAnonymous ? self : null);
        },
        pipeline () {
            return {
                operator: {
                    identified: 'visitorId',
                },
            };
        },
    },
    anonymousVisitorsOnly: {
        order: 3,
        default: true,
        name: $t('segments.default.Everyone'),
        aside: $t('segments.default.aside.anonymousVisitorsOnly'),
        id: 'anonymousVisitorsOnly',
        shared: true,
        definition: 'anonymousVisitorsOnly',
        description: $t(
            'segments.default.anonymousVisitorsOnly.Includes only anonymous visitors who have not been mapped to a Visitor ID',
        ),
        enabled () {
            const self = this;
            const showAnonymous = hasSubscriptionFlag('showAnonymous');

            return Promise.resolve(showAnonymous ? self : null);
        },
        pipeline () {
            return {
                operator: {
                    identified: '!visitorId',
                },
            };
        },
    },
    blacklistOnly: {
        order: 4,
        default: true,
        get name () {
            return hasSubscriptionFlag('enableAccounts')
                ? $t('segments.default.Excluded Accounts & Visitors')
                : $t('segments.default.Excluded Visitors');
        },
        id: 'blacklistOnly',
        shared: true,
        definition: 'blacklistOnly',
        get description () {
            return hasSubscriptionFlag('enableAccounts')
                ? $t(
                    'segments.default.blacklistOnly.Visitors and accounts that are part of the Exclude List in your subscription settings',
                )
                : $t(
                    'segments.default.blacklistOnly.Visitors that are part of the Exclude List in your subscription settings',
                );
        },
        blacklist: 'only',
        async enabled () {
            const self = this;

            return Promise.resolve(self);
        },
        pipeline () {
            return null;
        },
    },
    noOne: {
        order: 5,
        default: true,
        name: $t('segments.default.No one'),
        id: 'noOne',
        shared: true,
        definition: 'noOne',
        enabled () {
            const self = this;

            return Promise.resolve(self);
        },
        pipeline () {
            return [
                {
                    source: {
                        visitors: null,
                    },
                },
                {
                    filter: 'false',
                },
            ];
        },
    },
});

// Do no use constant - will be deprecated in a future change.
// Instead use getDefaultSegments() to account for user's current locale.
export const DEFAULT_SEGMENTS = getDefaultSegments();

export const SEGMENT_RULE_LIMIT = 5;

export function getIdsWithResources () {
    return [
        'page',
        'feature',
        'trackType',
        'guide',
        'guideExperiment',
        'guideElement',
        'email',
        'resourceCenter',
        'guideStepId',
        'poll',
        'feedbackRequest',
        'segment',
        'idea',
        'workflow',
        'journey',
        'survey',
    ];
}

export function getAppLevelEventRules () {
    return [
        'visitor.eventCount',
        'visitor.eventTime',
        'visitor.daysActive',
        'account.eventCount',
        'account.eventTime',
        'account.daysActive',
        'account.numVisitors',
        'parentAccount.eventCount',
        'parentAccount.eventTime',
        'parentAccount.daysActive',
        'parentAccount.numVisitors',
        'visitor.deadClicks',
        'visitor.errorClicks',
        'visitor.rageClicks',
        'visitor.uTurns',
        'account.deadClicks',
        'account.errorClicks',
        'account.rageClicks',
        'account.uTurns',
        'parentAccount.deadClicks',
        'parentAccount.errorClicks',
        'parentAccount.rageClicks',
        'parentAccount.uTurns',
    ];
}

export function isLastVisitRule ({ field }) {
    return (
        field === 'visitor.auto.lastvisit' ||
        field === 'account.auto.lastvisit' ||
        field === 'parentAccount.auto.lastvisit'
    );
}

export function isAccountIdsRule (filter) {
    return /visitor\.auto\.accountids?/.test(filter.field);
}

export function isSupportedGuideFilter (filter) {
    if (has(filter, 'eventPropertyField') || has(filter, 'eventPropertyName')) {
        return false;
    }

    if (filter.schema === 'aggregation') {
        return false;
    }

    if (isAccountIdsRule(filter)) {
        return false;
    }

    if (isLastVisitRule(filter)) {
        return false;
    }

    if (filter.schema === 'feedbackRequest') {
        return false;
    }

    if (filter.schema === 'survey' && !filter.guideId) {
        return false;
    }

    if (filter.group === 'utm') {
        return false;
    }

    if (has(filter, 'experimentId')) {
        return false;
    }

    return true;
}

export function hasNoAccountRules (filters) {
    return filters.every((filter) => filter.kind === 'visitor');
}

export async function getSegmentName (segmentId) {
    if (segmentId == null) {
        return $t('segments.default.Everyone');
    }

    if (isDefaultSegment(segmentId)) {
        const { name, aside } = getDefaultSegment(segmentId);
        if (aside) {
            return `${name} ${aside}`;
        }

        return name;
    }

    if (isString(segmentId)) {
        await store.dispatch('segments/loadAll');
        const segment = store.getters['segments/segmentById'](segmentId);

        if (segment) {
            return segment.name;
        }

        return $t('Unknown Segment');
    }
}

export function getBlacklistFromSegment (segmentId) {
    if (getDefaultSegments()[segmentId]) {
        return getDefaultSegments()[segmentId].blacklist || 'apply';
    }

    return 'apply';
}

export function isExcludingAnonymous (segmentId) {
    return segmentId === getDefaultSegments().everyoneExcludingAnonymous.id;
}

export function isEveryone (segmentId) {
    return segmentId === getDefaultSegments().everyone.id;
}

export function isDefaultSegmentEnabled (segmentId) {
    return getDefaultSegments()[segmentId] && getDefaultSegments()[segmentId].enabled();
}

export function getDefaultSegmentPipeline (segmentId) {
    return getDefaultSegments()[segmentId] && getDefaultSegments()[segmentId].pipeline;
}

export async function getEnabledDefaultSegments (removeBlacklist) {
    const segmentPromises = Object.values(getDefaultSegments()).map((segment) => segment.enabled());

    const segments = await Promise.all(segmentPromises);

    const enabled = segments.filter(Boolean);

    if (removeBlacklist) {
        return enabled.filter((segment) => segment.id !== 'blacklistOnly');
    }

    return enabled;
}

export function getDefaultSegment (segmentId) {
    return getDefaultSegments()[segmentId];
}

export function isDefaultSegment (segmentId) {
    return !!getDefaultSegments()[segmentId];
}

export function getAllDefaultSegments () {
    return cloneDeep(Object.values(getDefaultSegments()));
}

export function getIdentify (segmentId) {
    const showAnonymous = hasSubscriptionFlag('showAnonymous');

    if (showAnonymous) {
        switch (segmentId) {
            case getDefaultSegments().everyoneExcludingAnonymous.id:
                return 'visitorId';
            case getDefaultSegments().anonymousVisitorsOnly.id:
                return '!visitorId';
            default:
                return null;
        }
    }

    return 'visitorId';
}

export function parseSegmentParams (params) {
    const { segmentId } = params;

    return {
        blacklist: getBlacklistFromSegment(segmentId),
        includeAnonymous: getIdentify(segmentId),
        ...params,
        segmentId: isDefaultSegment(segmentId) ? null : segmentId,
    };
}

export function buildRuleTypes (rules, guideFriendly) {
    const apps = store.getters['apps/list'];
    const hasEmailElementFlag = hasSegmentFlag('emailElementSegment');

    return rules
        .filter((rule) => {
            if (guideFriendly) {
                return isSupportedGuideFilter(rule);
            }

            return rule.field !== 'visitor.auto.accountid';
        })
        .filter((rule) => rule.field !== 'visitor.auto.idhash')
        .map((rule) => {
            // type omits the group field, so use field instead
            rule.type = rule.field;

            // Add some special schema strings for metadata that has hard-coded
            // value arrays on the frontend (similar to Device Type concatenated
            // below)
            const schemaOverride = {
                visitor_auto_lastoperatingsystem: 'os',
                visitor_auto_lastbrowsername: 'name',
            }[rule.id];
            rule.schema = schemaOverride || rule.schema;

            return rule;
        })
        .concat(
            ...[
                {
                    id: 'segment',
                    name: $t('segments.ruleTypes.Segment'),
                    schema: 'segment',
                    type: 'segment',
                    kind: 'segment',
                    icon: { type: 'pie-chart' },
                },
                {
                    id: 'application',
                    name: $t('segments.ruleTypes.Application usage'),
                    schema: 'application',
                    type: 'application',
                    kind: 'visitor',
                    icon: { type: 'activity' },
                },
                {
                    id: 'workflow',
                    name: $t('segments.ruleTypes.Workflow progress'),
                    schema: 'workflow',
                    type: 'workflow',
                    kind: 'visitor',
                    icon: { type: 'adopt-workflows' },
                },
                {
                    id: 'feature',
                    name: $t('segments.ruleTypes.Feature'),
                    schema: 'aggregation',
                    type: 'feature',
                    kind: 'visitor',
                },
                {
                    id: 'page',
                    name: $t('segments.ruleTypes.Page'),
                    schema: 'aggregation',
                    type: 'page',
                    kind: 'visitor',
                },
                {
                    id: 'poll',
                    name: $t('segments.ruleTypes.Poll response'),
                    schema: 'poll',
                    type: 'poll',
                    kind: 'visitor',
                },
                {
                    id: 'guideExperiment',
                    name: $t('segments.ruleTypes.Experiment'),
                    schema: 'guide',
                    type: 'guideExperiment',
                    kind: 'visitor',
                    icon: { type: 'beaker' },
                },
                {
                    id: 'guide',
                    name: $t('segments.ruleTypes.Guide'),
                    schema: 'guide',
                    type: 'guide',
                    kind: 'visitor',
                },
                {
                    id: 'guideElement',
                    name: $t('segments.ruleTypes.Guide element'),
                    schema: 'aggregation',
                    type: 'guideElement',
                    kind: 'visitor',
                },
                {
                    id: 'resourceCenter',
                    name: $t('segments.ruleTypes.Resource Center'),
                    schema: 'guide',
                    type: 'guide',
                    kind: 'visitor',
                },
                {
                    id: 'trackType',
                    name: $t('segments.ruleTypes.Track Event'),
                    schema: 'aggregation',
                    type: 'trackType',
                    kind: 'visitor',
                },
                {
                    id: 'deviceType',
                    name: $t('segments.ruleTypes.Device type'),
                    schema: 'deviceType',
                    type: 'useragent',
                    kind: 'visitor',
                },
                {
                    id: 'sdkVersion',
                    name: $t('segments.ruleTypes.SDK version'),
                    schema: 'string',
                    type: 'useragent',
                    kind: 'visitor',
                    field: 'sdkVersion',
                    platforms: ['ios', 'android'],
                },
                {
                    id: 'appVersion',
                    name: $t('segments.ruleTypes.App version'),
                    schema: 'string',
                    type: 'useragent',
                    kind: 'visitor',
                    field: 'appVersion',
                    platforms: ['ios', 'android'],
                },
                {
                    id: 'deviceModel',
                    name: $t('segments.ruleTypes.Device model'),
                    schema: 'deviceModel',
                    type: 'useragent',
                    kind: 'visitor',
                    field: 'name',
                    platforms: ['ios', 'android'],
                },
                {
                    id: 'osVersion',
                    name: $t('segments.ruleTypes.OS version'),
                    schema: 'osVersion',
                    type: 'useragent',
                    kind: 'visitor',
                    field: 'version',
                    platforms: ['ios', 'android'],
                },
                {
                    id: 'visitor.eventCount',
                    name: $t('segments.ruleTypes.Number of events'),
                    schema: 'aggregation',
                    type: 'visitor.eventCount',
                    kind: 'visitor',
                    fact: 'eventCount',
                },
                {
                    id: 'visitor.deadClicks',
                    name: $t('segments.ruleTypes.Number of dead clicks'),
                    schema: 'aggregation',
                    type: 'visitor.deadClicks',
                    kind: 'visitor',
                    fact: 'deadClicks',
                },
                {
                    id: 'visitor.errorClicks',
                    name: $t('segments.ruleTypes.Number of error clicks'),
                    schema: 'aggregation',
                    type: 'visitor.errorClicks',
                    kind: 'visitor',
                    fact: 'errorClicks',
                },
                {
                    id: 'visitor.rageClicks',
                    name: $t('segments.ruleTypes.Number of rage clicks'),
                    schema: 'aggregation',
                    type: 'visitor.rageClicks',
                    kind: 'visitor',
                    fact: 'rageClicks',
                },
                {
                    id: 'visitor.uTurns',
                    name: $t('segments.ruleTypes.Number of u-turns'),
                    schema: 'aggregation',
                    type: 'visitor.uTurns',
                    kind: 'visitor',
                    fact: 'uTurns',
                },
                {
                    id: 'visitor.eventTime',
                    name: $t('segments.ruleTypes.Time on site'),
                    schema: 'aggregation',
                    type: 'visitor.eventTime',
                    kind: 'visitor',
                    fact: 'eventTime',
                },
                {
                    id: 'visitor.daysActive',
                    name: $t('segments.ruleTypes.Number of days active'),
                    schema: 'aggregation',
                    type: 'visitor.daysActive',
                    kind: 'visitor',
                    fact: 'daysActive',
                },
                {
                    id: 'account.numVisitors',
                    name: $t('segments.ruleTypes.Number of visitors'),
                    schema: 'aggregation',
                    type: 'account.numVisitors',
                    kind: 'account',
                    fact: 'numVisitors',
                },
                {
                    id: 'account.eventCount',
                    name: $t('segments.ruleTypes.Number of events'),
                    schema: 'aggregation',
                    type: 'account.eventCount',
                    kind: 'account',
                    fact: 'eventCount',
                },
                {
                    id: 'account.deadClicks',
                    name: $t('segments.ruleTypes.Number of dead clicks'),
                    schema: 'aggregation',
                    type: 'account.deadClicks',
                    kind: 'account',
                    fact: 'deadClicks',
                },
                {
                    id: 'account.errorClicks',
                    name: $t('segments.ruleTypes.Number of error clicks'),
                    schema: 'aggregation',
                    type: 'account.errorClicks',
                    kind: 'account',
                    fact: 'errorClicks',
                },
                {
                    id: 'account.rageClicks',
                    name: $t('segments.ruleTypes.Number of rage clicks'),
                    schema: 'aggregation',
                    type: 'account.rageClicks',
                    kind: 'account',
                    fact: 'rageClicks',
                },
                {
                    id: 'account.uTurns',
                    name: $t('segments.ruleTypes.Number of u-turns'),
                    schema: 'aggregation',
                    type: 'account.uTurns',
                    kind: 'account',
                    fact: 'uTurns',
                },
                {
                    id: 'account.eventTime',
                    name: $t('segments.ruleTypes.Time on site'),
                    schema: 'aggregation',
                    type: 'account.eventTime',
                    kind: 'account',
                    fact: 'eventTime',
                },
                {
                    id: 'account.daysActive',
                    name: $t('segments.ruleTypes.Number of days active'),
                    schema: 'aggregation',
                    type: 'account.daysActive',
                    kind: 'account',
                    fact: 'daysActive',
                },
                {
                    id: 'parentAccount.numAccounts',
                    name: $t('segments.ruleTypes.Number of accounts'),
                    schema: 'aggregation',
                    type: 'parentAccount.numAccounts',
                    kind: 'parentAccount',
                    fact: 'numAccounts',
                },
                {
                    id: 'parentAccount.numVisitors',
                    name: $t('segments.ruleTypes.Number of visitors'),
                    schema: 'aggregation',
                    type: 'parentAccount.numVisitors',
                    kind: 'parentAccount',
                    fact: 'numVisitors',
                },
                {
                    id: 'parentAccount.eventCount',
                    name: $t('segments.ruleTypes.Number of events'),
                    schema: 'aggregation',
                    type: 'parentAccount.eventCount',
                    kind: 'parentAccount',
                    fact: 'eventCount',
                },
                {
                    id: 'parentAccount.deadClicks',
                    name: $t('segments.ruleTypes.Number of dead clicks'),
                    schema: 'aggregation',
                    type: 'parentAccount.deadClicks',
                    kind: 'parentAccount',
                    fact: 'deadClicks',
                },
                {
                    id: 'parentAccount.errorClicks',
                    name: $t('segments.ruleTypes.Number of error clicks'),
                    schema: 'aggregation',
                    type: 'parentAccount.errorClicks',
                    kind: 'parentAccount',
                    fact: 'errorClicks',
                },
                {
                    id: 'parentAccount.rageClicks',
                    name: $t('segments.ruleTypes.Number of rage clicks'),
                    schema: 'aggregation',
                    type: 'parentAccount.rageClicks',
                    kind: 'parentAccount',
                    fact: 'rageClicks',
                },
                {
                    id: 'parentAccount.uTurns',
                    name: $t('segments.ruleTypes.Number of u-turns'),
                    schema: 'aggregation',
                    type: 'parentAccount.uTurns',
                    kind: 'parentAccount',
                    fact: 'uTurns',
                },
                {
                    id: 'parentAccount.eventTime',
                    name: $t('segments.ruleTypes.Time on site'),
                    schema: 'aggregation',
                    type: 'parentAccount.eventTime',
                    kind: 'parentAccount',
                    fact: 'eventTime',
                },
                {
                    id: 'parentAccount.daysActive',
                    name: $t('segments.ruleTypes.Number of days active'),
                    schema: 'aggregation',
                    type: 'parentAccount.daysActive',
                    kind: 'parentAccount',
                    fact: 'daysActive',
                },
                {
                    id: 'feedbackRequest',
                    name: $t('segments.ruleTypes.Feedback request'),
                    schema: 'feedbackRequest',
                    type: 'feedbackRequest',
                    kind: 'visitor',
                },
                {
                    id: 'idea',
                    name: $t('segments.ruleTypes.Idea'),
                    schema: 'counter',
                    type: 'idea',
                    kind: 'visitor',
                },
                {
                    id: 'survey',
                    name: $t('segments.ruleTypes.Sentiment'),
                    schema: 'survey',
                    type: 'survey',
                    kind: 'visitor',
                    icon: { type: 'surveys' },
                },
                {
                    id: 'journey',
                    name: $t('segments.ruleTypes.Journey'),
                    schema: 'journey',
                    type: 'journey',
                    kind: 'visitor',
                    icon: { type: 'orchestrate' },
                },
                {
                    id: 'email',
                    name: $t('segments.ruleTypes.Email'),
                    schema: 'email',
                    type: 'email',
                    kind: 'visitor',
                    icon: { type: 'mail' },
                },
                hasEmailElementFlag && {
                    id: 'emailElement',
                    name: $t('segments.ruleTypes.Email element'),
                    schema: 'aggregation',
                    type: 'emailElement',
                    kind: 'visitor',
                    icon: { type: 'guideElement' },
                    segment: true,
                },
            ],
        )
        .filter(Boolean)
        .filter((rule) => !rule.platforms || apps.some((app) => rule.platforms.includes(app.platform)));
}

export function getGuideFriendlyRules () {
    return [
        {
            type: 'page',
            schema: 'aggregation',
            id: 'visitor.eventCount',
            name: $t('segments.facts.Number of Page views'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'visitor.deadClicks',
            name: $t('segments.facts.Number of dead clicks'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'visitor.errorClicks',
            name: $t('segments.facts.Number of error clicks'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'visitor.rageClicks',
            name: $t('segments.facts.Number of rage clicks'),
        },
        { type: 'page', schema: 'aggregation', id: 'visitor.uTurns', name: $t('segments.facts.Number of u-turns') },
        { type: 'page', schema: 'aggregation', id: 'visitor.eventTime', name: $t('segments.facts.Time on Page') },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'visitor.daysActive',
            name: $t('segments.facts.Number of days active'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'account.numVisitors',
            name: $t('segments.facts.Number of visitors'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'account.eventCount',
            name: $t('segments.facts.Number of Page views'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'account.deadClicks',
            name: $t('segments.facts.Number of dead clicks'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'account.errorClicks',
            name: $t('segments.facts.Number of error clicks'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'account.rageClicks',
            name: $t('segments.facts.Number of rage clicks'),
        },
        { type: 'page', schema: 'aggregation', id: 'account.uTurns', name: $t('segments.facts.Number of u-turns') },
        { type: 'page', schema: 'aggregation', id: 'account.eventTime', name: $t('segments.facts.Time on Page') },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'account.daysActive',
            name: $t('segments.facts.Number of days active'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'parentAccount.numAccounts',
            name: $t('segments.facts.Number of accounts'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'parentAccount.numVisitors',
            name: $t('segments.facts.Number of visitors'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'parentAccount.eventCount',
            name: $t('segments.facts.Number of Page views'),
        },
        { type: 'page', schema: 'aggregation', id: 'parentAccount.eventTime', name: $t('segments.facts.Time on Page') },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'parentAccount.deadClicks',
            name: $t('segments.facts.Number of dead clicks'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'parentAccount.errorClicks',
            name: $t('segments.facts.Number of error clicks'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'parentAccount.rageClicks',
            name: $t('segments.facts.Number of rage clicks'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'parentAccount.uTurns',
            name: $t('segments.facts.Number of u-turns'),
        },
        {
            type: 'page',
            schema: 'aggregation',
            id: 'parentAccount.daysActive',
            name: $t('segments.facts.Number of days active'),
        },

        {
            type: 'feature',
            schema: 'aggregation',
            id: 'visitor.eventCount',
            name: $t('segments.facts.Number of clicks'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'visitor.deadClicks',
            name: $t('segments.facts.Number of dead clicks'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'visitor.errorClicks',
            name: $t('segments.facts.Number of error clicks'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'visitor.rageClicks',
            name: $t('segments.facts.Number of rage clicks'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'visitor.daysActive',
            name: $t('segments.facts.Number of days active'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'account.numVisitors',
            name: $t('segments.facts.Number of visitors'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'account.eventCount',
            name: $t('segments.facts.Number of clicks'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'account.deadClicks',
            name: $t('segments.facts.Number of dead clicks'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'account.errorClicks',
            name: $t('segments.facts.Number of error clicks'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'account.rageClicks',
            name: $t('segments.facts.Number of rage clicks'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'account.daysActive',
            name: $t('segments.facts.Number of days active'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'parentAccount.numAccounts',
            name: $t('segments.facts.Number of accounts'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'parentAccount.numVisitors',
            name: $t('segments.facts.Number of visitors'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'parentAccount.eventCount',
            name: $t('segments.facts.Number of clicks'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'parentAccount.deadClicks',
            name: $t('segments.facts.Number of dead clicks'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'parentAccount.errorClicks',
            name: $t('segments.facts.Number of error clicks'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'parentAccount.rageClicks',
            name: $t('segments.facts.Number of rage clicks'),
        },
        {
            type: 'feature',
            schema: 'aggregation',
            id: 'parentAccount.daysActive',
            name: $t('segments.facts.Number of days active'),
        },

        {
            type: 'trackType',
            schema: 'aggregation',
            id: 'visitor.eventCount',
            name: $t('segments.facts.Number of Track Events'),
        },
        {
            type: 'trackType',
            schema: 'aggregation',
            id: 'visitor.daysActive',
            name: $t('segments.facts.Number of days active'),
        },
        {
            type: 'trackType',
            schema: 'aggregation',
            id: 'account.numVisitors',
            name: $t('segments.facts.Number of visitors'),
        },
        {
            type: 'trackType',
            schema: 'aggregation',
            id: 'account.eventCount',
            name: $t('segments.facts.Number of Track Events'),
        },
        {
            type: 'trackType',
            schema: 'aggregation',
            id: 'account.daysActive',
            name: $t('segments.facts.Number of days active'),
        },
        {
            type: 'trackType',
            schema: 'aggregation',
            id: 'parentAccount.numAccounts',
            name: $t('segments.facts.Number of accounts'),
        },
        {
            type: 'trackType',
            schema: 'aggregation',
            id: 'parentAccount.numVisitors',
            name: $t('segments.facts.Number of visitors'),
        },
        {
            type: 'trackType',
            schema: 'aggregation',
            id: 'parentAccount.eventCount',
            name: $t('segments.facts.Number of Track Events'),
        },
        {
            type: 'trackType',
            schema: 'aggregation',
            id: 'parentAccount.daysActive',
            name: $t('segments.facts.Number of days active'),
        },

        {
            type: 'guideElement',
            schema: 'aggregation',
            id: 'visitor.eventCount',
            name: $t('segments.facts.Number of clicks'),
        },

        {
            type: 'emailElement',
            schema: 'aggregation',
            id: 'visitor.eventCount',
            name: $t('segments.facts.Number of clicks'),
        },

        { type: 'idea', schema: 'counter', id: 'votes', name: $t('segments.facts.Voters on idea') },
    ];
}

export function getUsedTagRules () {
    return [
        { type: 'application', schema: 'application', id: 'visitor.used', name: $t('segments.facts.Used') },
        { type: 'application', schema: 'application', id: 'visitor.notused', name: $t('segments.facts.Not used') },
        { type: 'application', schema: 'application', id: 'visitor.lastused', name: $t('segments.facts.Last used') },

        { type: 'workflow', schema: 'workflow', id: 'visitor.completed', name: $t('segments.facts.Completed') },
        { type: 'workflow', schema: 'workflow', id: 'visitor.incomplete', name: $t('segments.facts.Incomplete') },
        { type: 'workflow', schema: 'workflow', id: 'visitor.notstarted', name: $t('segments.facts.Not started') },

        { type: 'page', schema: 'counter', id: 'visitor.used', name: $t('segments.facts.Seen') },
        { type: 'page', schema: 'counter', id: 'visitor.notused', name: $t('segments.facts.Not seen') },
        { type: 'page', schema: 'counter', id: 'visitor.lastused', name: $t('segments.facts.Last seen') },

        { type: 'feature', schema: 'counter', id: 'visitor.used', name: $t('segments.facts.Used') },
        { type: 'feature', schema: 'counter', id: 'visitor.notused', name: $t('segments.facts.Not used') },
        { type: 'feature', schema: 'counter', id: 'visitor.lastused', name: $t('segments.facts.Last used') },

        { type: 'trackType', schema: 'counter', id: 'visitor.used', name: $t('segments.facts.Used') },
        { type: 'trackType', schema: 'counter', id: 'visitor.notused', name: $t('segments.facts.Not used') },
        { type: 'trackType', schema: 'counter', id: 'visitor.lastused', name: $t('segments.facts.Last used') },

        { type: 'guide', schema: 'guide', id: 'visitor.viewed', name: $t('segments.facts.Viewed') },
        { type: 'guide', schema: 'guide', id: 'visitor.lastviewed', name: $t('segments.facts.Last viewed') },
        { type: 'guide', schema: 'guide', id: 'visitor.notviewed', name: $t('segments.facts.Not viewed') },

        { type: 'guideElement', schema: 'guideElement', id: 'visitor.clicked', name: $t('segments.facts.Clicked') },
        {
            type: 'guideElement',
            schema: 'guideElement',
            id: 'visitor.notclicked',
            name: $t('segments.facts.Not clicked'),
        },

        { type: 'journey', schema: 'journey', id: 'visitor.entered', name: $t('segments.facts.Entered') },
        { type: 'journey', schema: 'journey', id: 'visitor.notentered', name: $t("segments.facts.Didn't enter") },

        { type: 'email', schema: 'email', id: 'visitor.delivered', name: $t('segments.facts.Delivered') },
        { type: 'email', schema: 'email', id: 'visitor.opened', name: $t('segments.facts.Opened') },
        { type: 'email', schema: 'email', id: 'visitor.clicked', name: $t('segments.facts.Clicked') },
        { type: 'email', schema: 'email', id: 'visitor.notdelivered', name: $t('segments.facts.Not delivered') },
        { type: 'email', schema: 'email', id: 'visitor.notopened', name: $t('segments.facts.Not opened') },
        { type: 'email', schema: 'email', id: 'visitor.notclicked', name: $t('segments.facts.Not clicked') },
        { type: 'emailElement', schema: 'emailElement', id: 'visitor.clicked', name: $t('segments.facts.Clicked') },
        {
            type: 'emailElement',
            schema: 'emailElement',
            id: 'visitor.notclicked',
            name: $t('segments.facts.Not clicked'),
        },
    ];
}

export function getGlobalSegmentAggParams () {
    const segmentId = store.state.globalFilters.selectedSegmentId;

    return defaults(parseSegmentParams({ segmentId }), {
        includeAnonymous: null,
        blacklist: 'apply',
        segmentId: null,
    });
}

export function getSegmentAggParams (segmentId) {
    return defaults(parseSegmentParams({ segmentId }), {
        includeAnonymous: null,
        blacklist: 'apply',
        segmentId: null,
    });
}

export const MOBILE_VERSION_RULES_LIST = ['sdkVersion', 'appVersion', 'osVersion', 'deviceModel'];

export const filterHasAppIdMismatch = (targetAppId) => ({ id, appId, schema }) => {
    if (!appId && schema === 'deviceType') return false;

    return (
        targetAppId &&
        appId !== '*' &&
        appId !== targetAppId &&
        (MOBILE_VERSION_RULES_LIST.includes(id) || schema === 'deviceType')
    );
};

export const segmentFiltersHaveAppIdMismatch = (segment, targetAppId) =>
    (segment.definition?.filters ?? []).some(filterHasAppIdMismatch(targetAppId));

export function segmentRuleNotSupportedGuideText (filter) {
    const isSessionProperty = filter.id === 'utm';
    const propertyType = isSessionProperty ? 'session' : 'event';

    if (filter.eventPropertyName || isSessionProperty) {
        const messageKey = `segmentsUtil.segmentRuleNotSupportedGuideText.This segment can not be used with guides because it contains a rule that is built using ${propertyType} properties`;

        return $t(messageKey);
    }

    let submessage = filter.name;

    if (filter.id === 'segment') {
        submessage = $t('segmentsUtil.segmentRuleNotSupportedGuideText.Nested segment is incompatible with guides');
    } else if (filter.type === 'page' && filter.fact === 'eventCount') {
        submessage = $t('segmentsUtil.segmentRuleNotSupportedGuideText.Number of Page Views');
    } else if (filter.type === 'page' && filter.fact === 'eventTime') {
        submessage = $t('segmentsUtil.segmentRuleNotSupportedGuideText.Time on Page');
    } else if (filter.type === 'feature' || filter.type === 'guideElement' || filter.type === 'emailElement') {
        submessage = $t('segmentsUtil.segmentRuleNotSupportedGuideText.Number of Clicks');
    } else if (filter.type === 'email') {
        submessage = $t('segmentsUtil.segmentRuleNotSupportedGuideText.Email Activity');
    }

    return $t(
        'segmentsUtil.segmentRuleNotSupportedGuideText.This segment is using a rule that is incompatible with guides: {submessage}',
        { submessage },
    );
}

export function nestedRuleCount (segment) {
    return segment.definition.filters.reduce((count, filter) => {
        if (filter.or) {
            filter.or.forEach((filter) => {
                if (filter.kind === 'segment') count++;
            });
        } else if (filter.kind === 'segment') count++;

        return count;
    }, 0);
}

export function createEventPropertiesFromSegmentRules (segment) {
    const eventProperties = {};
    Object.assign(eventProperties, countSegmentRules(segment));
    Object.assign(eventProperties, checkForEventPropertyFilters(segment));

    return eventProperties;
}

export function flattenFilters (filters) {
    return filters.reduce((acc, filter) => {
        if (filter.or) {
            filter.or.forEach((filter) => {
                acc.push(filter);
            });
        } else {
            acc.push(filter);
        }

        return acc;
    }, []);
}

export function checkForEventPropertyFilters (segment) {
    const filterTypes = [];
    const filters = flattenFilters(segment.definition?.filters || []);
    filters.forEach((filter) => {
        if (filter.filterType && filterTypes.indexOf(filter.filterType) === -1 && filter.useEventProperty) {
            filterTypes.push(filter.filterType);
        }
    });

    return {
        hasFilter: !!filterTypes.length,
        hasFilterTypes: filterTypes.toString(),
    };
}

export function getEventPropertyFilters (segment, filterByType = undefined) {
    const filters = flattenFilters(segment.definition?.filters || []);

    return filters.filter((filter) => {
        if (!filter.useEventProperty) return false;

        if (filterByType) {
            return filter.filterType === filterByType;
        }

        return true;
    });
}

export function countSegmentRules (segment) {
    let anonymousVisitorData;
    if (segment && segment.definition && segment.definition.advancedOptions) {
        if (segment.definition.advancedOptions.selectedVisitorType === 'visitorId') {
            anonymousVisitorData = 'Identified only';
        } else if (segment.definition.advancedOptions.selectedVisitorType === '!visitorId') {
            anonymousVisitorData = 'Anonymous only';
        } else {
            anonymousVisitorData = 'All visitors';
        }
    }

    const filters = segment.definition?.filters || [];
    const counts = filters.reduce(
        (acc, filter) => {
            if (filter.or) {
                acc.totalRuleCount += filter.or.length;
                filter.or.forEach((filter) => {
                    if (filter.kind === 'segment') {
                        acc.segmentRuleCount++;
                    } else if (filter.kind === 'account') {
                        acc.accountRuleCount++;
                    } else if (filter.kind === 'visitor') {
                        acc.visitorRuleCount++;
                    }
                });
            } else if (filter.kind === 'segment') {
                acc.totalRuleCount++;
                acc.segmentRuleCount++;
            } else {
                acc.totalRuleCount++;
                if (filter.kind === 'account') {
                    acc.accountRuleCount++;
                } else if (filter.kind === 'visitor') {
                    acc.visitorRuleCount++;
                }
            }

            return acc;
        },
        {
            totalRuleCount: 0,
            segmentRuleCount: 0,
            visitorRuleCount: 0,
            accountRuleCount: 0,
        },
    );

    return {
        ...counts,
        anonymousVisitorData,
        hasSegmentRules: counts.segmentRuleCount > 0,
        hasAccountRules: counts.accountRuleCount > 0,
        hasVisitorRules: counts.visitorRuleCount > 0,
    };
}

export function hasCSVRuleOperator (segment) {
    const filters = segment.definition?.filters || [];

    return filters.some((rule) => {
        if (rule.or) {
            return rule.or.some((subrule) => subrule.operator?.includes('listContains'));
        }

        return rule.operator?.includes('listContains');
    });
}

export function getSegmentSaveNotificationConfig (segment) {
    const sanitizeConfig = {
        allowedTags: [],
        disallowedTagsMode: 'escape',
    };
    const escapedSegmentId = sanitizeHtml(segment.id, sanitizeConfig);

    return {
        type: 'success',
        customClass: 'segment-created-notification',
        title: $t('segments.Segment Created Successfully!'),
        message: $t('segments.notifcationConfigMessage', {
            linkOpen: `<a href="/segments?segment=${escapedSegmentId}" target="_blank">`,
            linkClose: '</a>',
        }),
        dangerouslyUseHTMLString: true,
        duration: 3000,
    };
}

export function filtersHaveRuleWithFactKey (filters) {
    return filters.some((rule) => {
        if (rule.or) {
            return rule.or.some((subrule) => Boolean(subrule.fact));
        }

        return Boolean(rule.fact);
    });
}

// Let's be clear, this entire function is a big guess. Segments in engage have evolved a ton over the past 5 years,
// so there might be odd edges that we don't catch here (yet). If you find a new scenario where
// we incorrectly identify a segment as adopt/engage, slide it in here!
export function filterRuleMightHaveBeenMadeInAdopt (filterRule, segmentLastUpdatedDate) {
    // adopt filter rules attach an icon object to each rule, but engage segments do not
    const hasIcon = filterRule.icon;
    if (hasIcon) return true;

    // $valid and key were added as part of a large refactor in 2020 - ancient engage segments won't have these keys
    const timestampOfValidInCodebase = 1587441600000; // 04/21/2020
    const segmentCanHaveValidOrKey = segmentLastUpdatedDate >= timestampOfValidInCodebase;
    const ruleDoesNotHaveValidOrKey = !filterRule.$valid && !filterRule.key;

    if (segmentCanHaveValidOrKey && ruleDoesNotHaveValidOrKey) return true;

    return false;
}

export function isAdoptSegment (segment) {
    return segment.isCreatedFromAdoptUI;
}

export function rulesColumnCSVFormatter ({ rules }) {
    const { unseenNumOfRules, summary } = rules;
    if (unseenNumOfRules) {
        return `${summary} + ${unseenNumOfRules} more ${unseenNumOfRules > 1 ? 'rules' : 'rule'}`;
    }

    return summary;
}

export function segmentRulesColumnCSVFormatter (row) {
    const rules = row?.segment?.rules;
    if (!rules) {
        return row?.segment?.name || '';
    }

    return rulesColumnCSVFormatter({ rules });
}

export function isEmailSegmentGuideEligible (segment) {
    if (!segment) return false;
    if (segment.compound) {
        if (segment.pipelines) {
            return Object.keys(segment.pipelines).every((pipelineKey) =>
                isEmailSegmentGuideEligible({ pipeline: segment.pipelines[pipelineKey] }),
            );
        }

        return true;
    }
    if (!segment.pipeline) return false;

    return !segment.pipeline.some((operator) =>
        operator.spawn?.some((group) => group.some((item) => item.source?.emailEvents)),
    );
}

export async function getNestedSegments (compounds) {
    const nestedSegmentIds = compounds.reduce((acc, compound) => {
        if (Array.isArray(compound)) {
            compound.forEach((condition) => {
                if (condition.segmentId) {
                    acc.push(condition.segmentId);
                }
            });
        }

        return acc;
    }, []);

    const nestedSegments = nestedSegmentIds.length ? await Promise.all(nestedSegmentIds.map(async (segmentId) => {
        await store.dispatch('segments/loadOne', { id: segmentId });

        return store.getters['segments/segmentById'](segmentId);
    })) : [];

    return nestedSegments;
}

export const APP_SPECIFIC_AUTO_METADATA_IDS = [
    'visitor_auto_firstidentifiedvisit',
    'visitor_auto_firstanonymousvisit',
    'visitor_auto_firstvisit',
    'visitor_auto_lastvisit',
    'account_auto_firstvisit',
    'account_auto_lastvisit',
];

export const NEW_USAGE_RULES_LIST = [
    'visitor_auto_firstidentifiedvisit',
    'visitor_auto_firstvisit',
    'account_auto_firstvisit',
    'parentAccount_auto_firstvisit',
];
